/**
 * src/lib/useAppData.js — App-wide data orchestrator (raw snake_case shape)
 * AC Nautitech.
 *
 * Thin orchestrator that composes 5 domain hooks and coordinates
 * boot/reload/merge across all domains. Domain hooks own their own
 * state, mutations, fetch, and merge logic.
 *
 * Phase 81-01: Extracted state + mutations into domain hooks.
 * Phase 81-02: Extracted fetch/merge into domain hooks. This file
 *              is now pure orchestration + cross-domain lookups.
 *
 * Returns raw Supabase rows. See docs/SHAPE_CONTRACT.md.
 */

import { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import { useToast } from '../components/shared/ToastProvider';
import * as Sentry from '@sentry/react';
import { timedFetch, logAppError } from './errorReporter';
import { logReload } from './observability';
import { useLookups } from './lookups/LookupContext';

// Domain hooks — each owns state + mutations + fetch + merge
import { useTaskData } from './hooks/useTaskData';
import { useProjectData } from './hooks/useProjectData';
import { useCRMData } from './hooks/useCRMData';
import { useProcurementData } from './hooks/useProcurementData';
import { useDirectoryData } from './hooks/useDirectoryData';
import { subscribe as subscribeSync, shouldReconcileAfterSync } from './offline/syncEngine';
import { subscribe as subscribeNet } from './offline/networkStatus';
import AuthContext from '../auth/AuthContext';

// TICKET-1100: the full load must always END. On iOS a crashed WebKit networking
// process leaves in-flight reads un-settled forever (edge logs 2026-10-05: the
// retry's /rest/v1/tasks never left the phone), and an unbounded await here kept
// `loading` true -> skeleton until force-quit. Timeout -> catch below -> error
// banner + loading false + one scheduled background retry.
export const BOOT_LOAD_TIMEOUT_MS = 25000;

function withBootTimeout(promise) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('Data load timed out')), BOOT_LOAD_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export function useAppData({ offlineEnabled = false } = {}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Null-safe: some test environments render without <AuthProvider>.
  // authCtx=null → authLoading=false → boot fires immediately (backward-compat).
  const authCtx = useContext(AuthContext);

  const { crmStages, projectStages, projectTypes: lookupProjectTypes, loading: lookupsLoading } = useLookups();

  const stages = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const s of [...projectStages, ...crmStages]) {
      if (!seen.has(s.id)) {
        seen.add(s.id);
        combined.push(s);
      }
    }
    return combined;
  }, [crmStages, projectStages]);

  const projectTypes = lookupProjectTypes;

  const reloadTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const optimisticTimestamps = useRef(new Map());
  const silentReloadRef = useRef(null);

  const { showError } = useToast();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    };
  }, []);

  // ─── scheduleReload ────────────────────────────────────────────
  const scheduleReload = useCallback((delayMs = 2000, trigger = 'unknown') => {
    logReload(trigger, { delayMs });
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => {
      if (silentReloadRef.current) silentReloadRef.current();
    }, delayMs);
  }, []);

  // ─── Domain hooks ─────────────────────────────────────────────
  const taskData = useTaskData({ scheduleReload, showError, offlineEnabled });
  const projectData = useProjectData({ scheduleReload, stages, projectTypes, showError, offlineEnabled });
  const crmData = useCRMData({ scheduleReload, showError, stages, offlineEnabled });
  const procurementData = useProcurementData({ scheduleReload, offlineEnabled });
  const directoryData = useDirectoryData({ offlineEnabled });

  // ─── Reload orchestration ─────────────────────────────────────
  const reload = useCallback(async () => {
    const bootStart = performance.now();
    setLoading(true);
    setError(null);
    try {
      const fetchAllDomains = () => Promise.all([
        taskData.fetchAll(),
        projectData.fetchAll(),
        crmData.fetchAll(),
        procurementData.fetchAll(),
        directoryData.fetchAll(),
      ]);

      let [taskResult, projResult, crmResult, procResult, dirResult] = await timedFetch('appData:fullReload', () => withBootTimeout(fetchAllDomains()));

      // Retry-once: if critical domains (tasks, projects) both came back empty
      if ((!taskResult.tasks?.length) && (!projResult.projects?.length)) {
        console.warn('[AppData] Critical domains empty on first load — retrying in 500ms');
        await new Promise(res => setTimeout(res, 500));
        [taskResult, projResult, crmResult, procResult, dirResult] = await withBootTimeout(fetchAllDomains());
      }

      if (!mountedRef.current) return;

      // Each domain applies its own data
      taskData.applyFetchResult(taskResult);
      projectData.applyFetchResult(projResult);
      crmData.applyFetchResult(crmResult);
      procurementData.applyFetchResult(procResult);
      directoryData.applyFetchResult(dirResult);

      // Shape contract checks
      const shapeChecks = [
        { name: 'tasks',    data: taskResult.tasks,       cols: ['id', 'task_name', 'status', 'project_id', 'assignee_id', 'due_date'] },
        { name: 'projects', data: projResult.projects,    cols: ['id', 'project_name', 'stage_id', 'project_type_id'] },
        { name: 'crm',      data: crmResult.crm,          cols: ['id', 'opportunity', 'stage_id', 'vessel_id'] },
        { name: 'vessels',   data: crmResult.vessels,      cols: ['id', 'vessel_name'] },
        { name: 'bundles',   data: projResult.bundles,     cols: ['id', 'title'] },
      ];
      for (const { name, data, cols } of shapeChecks) {
        if (!Array.isArray(data) || data.length === 0) continue;
        const sample = data[0];
        const missing = cols.filter(c => !(c in sample));
        if (missing.length > 0) {
          logAppError({
            type: 'schema_contract',
            message: `${name} missing columns: ${missing.join(', ')}`,
            metadata: { entity: name, missing, sampleKeys: Object.keys(sample).slice(0, 10) },
          });
        }
      }
    } catch (err) {
      console.warn('[AppData] Load error:', err.message);
      Sentry.captureException(err);
      if (mountedRef.current) setError(err.message);
      // Self-heal once the browser's network stack recovers (bounded: one shot per failed reload).
      if (mountedRef.current) scheduleReload(5000, 'boot-load-failed');
    }
    const bootMs = Math.round(performance.now() - bootStart);
    if (bootMs > 5000) {
      logAppError({ type: 'slow_boot', message: `AppData reload took ${bootMs}ms`, metadata: { duration_ms: bootMs } });
    }
    console.debug(`[AppData] reload: ${bootMs}ms`);
    if (mountedRef.current) setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable: fetchAll/applyFetchResult/mergeAll are useCallback-wrapped
  }, [taskData.fetchAll, taskData.applyFetchResult, projectData.fetchAll, projectData.applyFetchResult, crmData.fetchAll, crmData.applyFetchResult, procurementData.fetchAll, procurementData.applyFetchResult, directoryData.fetchAll, directoryData.applyFetchResult]);

  // ─── Silent reload (background merge with optimistic preservation) ─
  const silentReload = useCallback(async () => {
    try {
      const [taskResult, projResult, crmResult, procResult, dirResult] = await Promise.all([
        taskData.fetchAll().catch(() => null),
        projectData.fetchAll().catch(() => null),
        crmData.fetchAll().catch(() => null),
        procurementData.fetchAll().catch(() => null),
        directoryData.fetchAll().catch(() => null),
      ]);
      if (!mountedRef.current) return;

      const now = Date.now();
      const ts = optimisticTimestamps.current;
      if (taskResult) taskData.mergeAll(taskResult, ts, now);
      if (projResult) projectData.mergeAll(projResult, ts, now);
      if (crmResult) crmData.mergeAll(crmResult, ts, now);
      if (procResult) procurementData.mergeAll(procResult, ts, now);
      if (dirResult) directoryData.mergeAll(dirResult, ts, now);
    } catch (err) {
      console.warn('[AppData] Silent reload error:', err.message);
      Sentry.captureException(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable: fetchAll/mergeAll are useCallback-wrapped
  }, [taskData.fetchAll, taskData.mergeAll, projectData.fetchAll, projectData.mergeAll, crmData.fetchAll, crmData.mergeAll, procurementData.fetchAll, procurementData.mergeAll, directoryData.fetchAll, directoryData.mergeAll]);

  silentReloadRef.current = silentReload;

  // Gate the cold-boot fetch on AuthContext being settled. On a slow SW cold
  // start, even the single-flight getSession() (partial fix 64cfc976) can
  // exceed the 8s timeout before AuthContext.init() has resolved the lock.
  // Waiting for authLoading=false means supabase-js's session is already in
  // its in-memory cache by the time fetchAll() calls getAuthHeaders() — zero
  // lock contention, fast response. (TICKET-231)
  const authLoading = authCtx?.loading ?? false;
  useEffect(() => {
    if (!authLoading) reload();
  }, [authLoading, reload]);

  // Offline reconcile: once the sync engine flushes the WHOLE outbox after
  // reconnecting, pull fresh server state so optimistic offline edits resolve
  // into real rows and the _pendingSync markers clear. silentReloadRef.current
  // is reassigned every render, so reading it at call time uses the latest.
  useEffect(() => {
    if (!offlineEnabled) return undefined;
    return subscribeSync((evt) => {
      if (shouldReconcileAfterSync(evt) && silentReloadRef.current) {
        silentReloadRef.current();
      }
    });
  }, [offlineEnabled]);

  // Plain reconnect refresh. The sync-engine reconcile above only fires AFTER the
  // outbox DRAINS, so a reconnect with no queued edits never refreshed the board
  // (CEO 2026-06-06: "no real-time update when you reconnect to soft refresh").
  // Subscribe directly to connectivity and silent-reload on the offline->online
  // transition so fresh server state lands regardless of whether anything was
  // queued. Ungated (every user benefits). silentReloadRef is reassigned each
  // render — read it at call time. subscribeNet only fires on an actual change.
  useEffect(() => {
    return subscribeNet((online) => {
      if (online && silentReloadRef.current) silentReloadRef.current();
    });
  }, []);

  // TICKET-1122: a PWA left open on the phone never refetched, so a deal created on
  // the laptop stayed invisible until a manual reload. Refresh when the app comes
  // back to the foreground (throttled — iOS fires visibilitychange liberally).
  // TICKET-1131: seed at 0, not mount time — a return within 30s of mount must still refresh.
  useEffect(() => {
    let last = 0;
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 30000) return;
      last = Date.now();
      if (silentReloadRef.current) silentReloadRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Explicit refresh signal. Server-side bulk creates (the Gmail lead sync) make
  // deals WITHOUT going through the client mutation path that calls scheduleReload,
  // so the board never refetched after a successful sync (CEO 2026-06-17: the new
  // lead only appeared on a manual reload). LeadSyncButton dispatches
  // 'acn:crm-refresh' on success — silent-reload so fresh server state lands.
  useEffect(() => {
    const onCrmRefresh = () => { if (silentReloadRef.current) silentReloadRef.current(); };
    window.addEventListener('acn:crm-refresh', onCrmRefresh);
    return () => window.removeEventListener('acn:crm-refresh', onCrmRefresh);
  }, []);

  // ─── Lookup maps (cross-domain derived state) ─────────────────
  const peopleLookup = useMemo(() => {
    const m = {};
    directoryData.people.forEach(p => { m[p.id] = p.display_name || p.email || ''; });
    return m;
  }, [directoryData.people]);

  const projectLookup = useMemo(() => {
    const m = {};
    projectData.projects.forEach(p => {
      const num = p.project_number ? `#${p.project_number} - ` : '';
      m[p.id] = `${num}${p.project_name || ''}`;
    });
    return m;
  }, [projectData.projects]);

  const stageLookup = useMemo(() => {
    const m = {};
    stages.forEach(s => { m[s.id] = s.label_en || s.stage_name || ''; });
    return m;
  }, [stages]);

  const projectTypeLookup = useMemo(() => {
    const m = {};
    projectTypes.forEach(pt => { m[pt.id] = pt.label_en || pt.slug || ''; });
    return m;
  }, [projectTypes]);

  const vesselLookup = useMemo(() => {
    const m = {};
    crmData.vessels.forEach(v => { m[v.id] = v; });
    return m;
  }, [crmData.vessels]);

  const crmLookup = useMemo(() => {
    const m = {};
    crmData.crm.forEach(d => { m[d.id] = d; });
    return m;
  }, [crmData.crm]);

  const flowMap = useMemo(() => {
    const map = {};
    projectData.stageFlows.forEach(sf => {
      map[sf.id] = {
        name: sf.flow_name || sf.label_en || '',
        slug: sf.slug || '',
        project_type_ids: sf.project_type_ids || [],
        flow_type: sf.flow_type || null,
        steps: [],
      };
    });
    (projectData.stageFlowSteps || [])
      .slice()
      .sort((a, b) => (a.step_order || 0) - (b.step_order || 0))
      .forEach(step => {
        const flowId = step.flow_id;
        const stageId = step.stage_id;
        const stageName = stageId ? (stageLookup[stageId] || '') : '';
        if (flowId && map[flowId] && stageName) map[flowId].steps.push(stageName);
      });
    return map;
  }, [projectData.stageFlows, projectData.stageFlowSteps, stageLookup]);

  // ─── Public return (identical shape to pre-refactor) ──────────
  return {
    tasks: taskData.tasks, projects: projectData.projects, people: directoryData.people,
    departments: directoryData.departments,
    stages, timeLogs: taskData.timeLogs, bundles: projectData.bundles,
    stageFlows: projectData.stageFlows, stageFlowSteps: projectData.stageFlowSteps,
    projectTypes, crm: crmData.crm, deals: crmData.crm, vessels: crmData.vessels,
    vesselContacts: crmData.vesselContacts, clients: crmData.clients,
    ports: directoryData.ports, suppliers: procurementData.suppliers,
    purchaseOrders: procurementData.purchaseOrders,
    peopleLookup, projectLookup, stageLookup, projectTypeLookup,
    flowMap, vesselLookup, crmLookup,
    loading: loading || lookupsLoading, error,
    reload, silentReload,
    handleUpdateTask: taskData.handleUpdateTask, handleUpdateStatus: taskData.handleUpdateStatus,
    handleUpdateAssignee: taskData.handleUpdateAssignee, handleCreateTask: taskData.handleCreateTask,
    handleDeleteTask: taskData.handleDeleteTask, handleArchiveTask: taskData.handleArchiveTask,
    handleCreateTimeEntry: taskData.handleCreateTimeEntry, refreshTasks: taskData.refreshTasks,
    handleCreateProject: projectData.handleCreateProject, handleMoveProjectStage: projectData.handleMoveProjectStage,
    handleUpdateProject: projectData.handleUpdateProject, handleUpdateBundle: projectData.handleUpdateBundle,
    handleCreateBundle: projectData.handleCreateBundle, handleDeleteBundle: projectData.handleDeleteBundle,
    handleUpdateDealStatus: crmData.handleUpdateDealStatus, handleUpdateDeal: crmData.handleUpdateDeal,
    handleUpdateVessel: crmData.handleUpdateVessel, handleCreateVessel: crmData.handleCreateVessel,
    handleCreateDeal: crmData.handleCreateDeal,
    handleCreateVesselContact: crmData.handleCreateVesselContact, handleUpdateVesselContact: crmData.handleUpdateVesselContact,
    handleDeleteVesselContact: crmData.handleDeleteVesselContact,
    handleCreateActivity: crmData.handleCreateActivity, handleUpdateActivity: crmData.handleUpdateActivity,
    handleCreateSupplier: procurementData.handleCreateSupplier, handleUpdateSupplier: procurementData.handleUpdateSupplier,
    handleCreatePurchaseOrder: procurementData.handleCreatePurchaseOrder,
    handleCreatePort: directoryData.handleCreatePort,
  };
}
